import React, { useState } from 'react';
import { formatLeverage } from './shared/formatting';
import EditableInput from './shared/EditableInput';
import { ALLOWED_LEVERAGE, CATEGORY_LEVERAGE_LIMITS, getMaxAllowedLeverageForCategory } from './shared/constants';

/**
 * Leverage Settings Section for Create/Edit Group Modal
 */
const LeverageSettingsSection = ({
  formData,
  formErrors,
  loading,
  onLeverageChange
}) => {
  const [editingField, setEditingField] = useState(null);
  const [tempValues, setTempValues] = useState({});
  const [showInfoTooltip, setShowInfoTooltip] = useState(null);

  const handleValueClick = (field, category = null) => {
    const fieldKey = category ? `${category}_${field}` : field;
    setEditingField(fieldKey);

    let currentValue;
    if (category) {
      currentValue = formData.leverageSettings?.[category]?.[field] || 1;
    } else {
      currentValue = formData.leverageSettings?.[field] || 1;
    }

    setTempValues(prev => ({
      ...prev,
      [fieldKey]: currentValue.toString()
    }));
  };

  const handleTempChange = (fieldKey, value) => {
    setTempValues(prev => ({
      ...prev,
      [fieldKey]: value
    }));
  };

  const handleKeyDown = (e, fieldKey, category = null, field = null) => {
    if (e.key === 'Enter') {
      handleSubmit(fieldKey, category, field);
    } else if (e.key === 'Escape') {
      handleCancel(fieldKey);
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      const currentValue = parseFloat(tempValues[fieldKey]) || 1;
      const minValue = getMinForField(category, field);
      const maxValue = getMaxForField(category, field);

      if (e.key === 'ArrowUp') {
        const nextAllowed = ALLOWED_LEVERAGE.find(val => val > currentValue && val <= maxValue);
        const newValue = nextAllowed || currentValue;
        handleTempChange(fieldKey, newValue.toString());
      } else {
        const prevAllowed = [...ALLOWED_LEVERAGE].reverse().find(val => val < currentValue && val >= minValue);
        const newValue = prevAllowed || minValue;
        handleTempChange(fieldKey, newValue.toString());
      }
    }
  };

  const handleSubmit = (fieldKey, category = null, field = null) => {
    const value = parseFloat(tempValues[fieldKey]) || 1;

    if (category && field) {
      onLeverageChange(category, field, value);
    }

    setEditingField(null);
    setTempValues(prev => {
      const newTemp = { ...prev };
      delete newTemp[fieldKey];
      return newTemp;
    });
  };

  const handleCancel = (fieldKey) => {
    setEditingField(null);
    setTempValues(prev => {
      const newTemp = { ...prev };
      delete newTemp[fieldKey];
      return newTemp;
    });
  };

  const handleInputBlur = (fieldKey, category = null, field = null) => {
    setTimeout(() => {
      if (editingField === fieldKey) {
        handleSubmit(fieldKey, category, field);
      }
    }, 200);
  };

  const getMinForField = (category, field) => {
    return 1;
  };

  const getMaxForField = (category, field) => {
    if (category) {
      if (field === 'currentLeverage') {
        const categoryMax = formData.leverageSettings?.[category]?.maxLeverage || getMaxAllowedLeverageForCategory(category);
        return categoryMax;
      }
      return getMaxAllowedLeverageForCategory(category);
    }
    return 500;
  };

  const renderInputField = (field, category = null) => {
    const fieldKey = category ? `${category}_${field}` : field;

    let currentValue;
    if (category) {
      currentValue = formData.leverageSettings?.[category]?.[field] || 1;
    } else {
      currentValue = formData.leverageSettings?.[field] || 1;
    }

    return (
      <EditableInput
        value={formatLeverage(currentValue)}
        isEditing={editingField === fieldKey}
        tempValue={tempValues[fieldKey] || ''}
        onEdit={() => handleValueClick(field, category)}
        onChange={(value) => handleTempChange(fieldKey, value)}
        onSubmit={() => handleSubmit(fieldKey, category, field)}
        onCancel={() => handleCancel(fieldKey)}
        onKeyDown={(e) => handleKeyDown(e, fieldKey, category, field)}
        onBlur={() => handleInputBlur(fieldKey, category, field)}
        min={getMinForField(category, field)}
        max={getMaxForField(category, field)}
        step="1"
      />
    );
  };

  const rangeClass = (key) =>
    `gm-lev-range ${formErrors[key] ? 'gm-form-error-input' : ''}`;

  return (
    <div className="gm-lev-grid">
      {Object.entries(formData.leverageSettings || {})
        .filter(([category]) => ['stocks', 'crypto', 'indices', 'commodities', 'forex'].includes(category))
        .map(([category, settings]) => {
          const maxLimit = getMaxAllowedLeverageForCategory(category);

          return (
            <div key={category} className="gm-lev-card">
              <div className="gm-lev-header">
                <span className="gm-lev-category">{category.toUpperCase()}</span>
                <span className="gm-lev-limit">Max: {maxLimit}:1</span>
              </div>
              <div>
                <div className="gm-lev-row">
                  <label>Max Leverage</label>
                  <div className="gm-lev-control">
                    <input
                      type="range"
                      min="1"
                      max={maxLimit}
                      step="1"
                      value={Math.min(settings?.maxLeverage || 1, maxLimit)}
                      onChange={(e) => onLeverageChange(category, 'maxLeverage', parseFloat(e.target.value))}
                      disabled={loading}
                      className={rangeClass(`leverage_${category}_maxLeverage`)}
                    />
                    <span className="gm-lev-value">{settings?.maxLeverage || 1}x</span>
                  </div>
                  {formErrors[`leverage_${category}_maxLeverage`] && (
                    <span className="gm-form-error-text">{formErrors[`leverage_${category}_maxLeverage`]}</span>
                  )}
                </div>
                <div className="gm-lev-row">
                  <label>Default Leverage</label>
                  <div className="gm-lev-control">
                    <input
                      type="range"
                      min="1"
                      max={settings?.maxLeverage || 1}
                      step="1"
                      value={Math.min(settings?.currentLeverage || 1, settings?.maxLeverage || 1)}
                      onChange={(e) => onLeverageChange(category, 'currentLeverage', parseFloat(e.target.value))}
                      disabled={loading}
                      className={rangeClass(`leverage_${category}_currentLeverage`)}
                    />
                    <span className="gm-lev-value">{settings?.currentLeverage || 1}x</span>
                  </div>
                  {formErrors[`leverage_${category}_currentLeverage`] && (
                    <span className="gm-form-error-text">{formErrors[`leverage_${category}_currentLeverage`]}</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
    </div>
  );
};

export default LeverageSettingsSection;
